import { useState, useEffect } from 'react';
import { formatDecimal } from '../lib/utils';
import { useNavigate, useParams } from 'react-router-dom';
import { useRef } from 'react';
import {
  Save,
  User,
  Building2,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  Euro,
  FileText,
  GraduationCap,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  Plus,
  MessageSquare,
  Shirt,
  HardHat,
  Upload,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { CollapsibleCard } from '../components/ui/collapsible-card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../core/apiClient';
import apiClient from '../core/apiClient';
import EmployeeCalendar from '../components/rrhh/EmployeeCalendar';

interface Employee {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  photoUrl?: string | null;
  dni: string;
  socialSecurityNumber?: string;
  birthDate?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  phone?: string;
  personalEmail?: string;
  workEmail?: string;
  hireDate: string;
  terminationDate?: string;
  contractType: string;
  department?: string;
  position?: string;
  category?: string;
  grossSalary?: number;
  hourlyCost: number;
  weeklyHours: number;
  iban?: string;
  drivingLicense?: string;
  emergencyContact?: string;
  emergencyPhone?: string;
  status: 'active' | 'on_leave' | 'terminated';
  notes?: string;
}

interface Training {
  id: string;
  training: {
    name: string;
    type: string;
  };
  startDate: string;
  endDate?: string;
  expiryDate?: string;
  status: string;
  certificateNumber?: string;
}

interface Contract {
  id: string;
  type: string;
  startDate: string;
  endDate?: string;
  grossSalary?: number;
  hourlyCost: number;
  isCurrent: number;
}

interface Document {
  id: string;
  type: string;
  name: string;
  fileName: string;
  documentDate?: string;
  expiryDate?: string;
}

// Catàlegs d'Admin > Taules > RRHH (Relació Laboral, Tipus de Contracte,
// Departament, Càrrec, Talla de Pantalons, Talla de Camisa).
interface CatalogOption {
  id: number;
  code: string;
  name: string;
  isActive: number;
}

// Dades editables del formulari — mateixos camps que abans tenia EmployeeForm.tsx (ara fusionat
// aquí: ja no hi ha una pagina de "nomes lectura" separada, igual que la resta de fitxes de
// l'ERP — Clients, Expedients Comercials, Pressupostos — que son una sola pantalla editable).
interface EmployeeFormData {
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  dni: string;
  dniIssueDate: string;
  dniExpiryDate: string;
  socialSecurityNumber: string;
  birthDate: string;
  address: string;
  city: string;
  postalCode: string;
  phone: string;
  personalEmail: string;
  workEmail: string;
  hireDate: string;
  terminationDate: string;
  // Catàleg "Tipus de Contracte" (Admin > Taules > RRHH) — codi lliure, no
  // tancat a un enum fix, per poder afegir tipus nous sense desplegament.
  contractType: string;
  department: string;
  position: string;
  category: string;
  grossSalary: number;
  hourlyCost: number;
  hourlyCostSaturday: number;
  hourlyCostHoliday: number;
  bonusOutsideBcn: number;
  correctionFactor: number;
  companySalaryCost: number;
  grossHourlyCost: number;
  weeklyHours: number;
  iban: string;
  drivingLicense: string;
  drivingLicenseExpiry: string;
  emergencyContact: string;
  emergencyPhone: string;
  notes: string;
  pantsSize: string;
  shirtSize: string;
  shoeSize: number | '';
  relacioLaboral: string;
  empresa: string;
  departamentEnum: string;
  carrec: string;
  riskInfoDate: string;
  machineAuthDate: string;
  epiDeliveryDate: string;
  medicalReviewDate: string;
}

const initialFormData: EmployeeFormData = {
  firstName: '',
  lastName: '',
  photoUrl: null,
  dni: '',
  dniIssueDate: '',
  dniExpiryDate: '',
  socialSecurityNumber: '',
  birthDate: '',
  address: '',
  city: '',
  postalCode: '',
  phone: '',
  personalEmail: '',
  workEmail: '',
  hireDate: new Date().toISOString().split('T')[0],
  terminationDate: '',
  contractType: 'indefinite',
  department: '',
  position: '',
  category: '',
  grossSalary: 0,
  hourlyCost: 0,
  hourlyCostSaturday: 0,
  hourlyCostHoliday: 0,
  bonusOutsideBcn: 0,
  correctionFactor: 0,
  companySalaryCost: 0,
  grossHourlyCost: 0,
  weeklyHours: 40,
  iban: '',
  drivingLicense: '',
  drivingLicenseExpiry: '',
  emergencyContact: '',
  emergencyPhone: '',
  notes: '',
  pantsSize: '',
  shirtSize: '',
  shoeSize: '',
  relacioLaboral: '',
  empresa: '',
  departamentEnum: '',
  carrec: '',
  riskInfoDate: '',
  machineAuthDate: '',
  epiDeliveryDate: '',
  medicalReviewDate: '',
};

const tabs = [
  { id: 'general', label: 'Dades', icon: User },
  { id: 'calendar', label: 'Calendari', icon: Calendar },
  { id: 'contracts', label: 'Contractes', icon: FileText },
  { id: 'training', label: 'Formació', icon: GraduationCap },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'time', label: 'Imputacions', icon: Clock },
];

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'success' }> = {
  active: { label: 'Actiu', variant: 'success' },
  on_leave: { label: 'De baixa', variant: 'secondary' },
  terminated: { label: 'Baixa', variant: 'destructive' },
};

const contractTypeLabels: Record<string, string> = {
  indefinite: 'Indefinit',
  temporary: 'Temporal',
  intern: 'Practiques',
  freelance: 'Autonom',
};

const trainingStatusConfig: Record<string, { label: string; icon: typeof CheckCircle; color: string }> = {
  scheduled: { label: 'Programada', icon: Calendar, color: 'text-blue-500' },
  in_progress: { label: 'En curs', icon: Clock, color: 'text-amber-500' },
  completed: { label: 'Completada', icon: CheckCircle, color: 'text-green-500' },
  failed: { label: 'No superada', icon: XCircle, color: 'text-red-500' },
  cancelled: { label: 'Cancel·lada', icon: XCircle, color: 'text-gray-500' },
};

export default function EmployeeDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState<EmployeeFormData>(initialFormData);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Catàlegs editables a Admin > Taules > RRHH (abans ENUM fixes al codi).
  const [relacioLaboralOpts, setRelacioLaboralOpts] = useState<CatalogOption[]>([]);
  const [tipusContracteOpts, setTipusContracteOpts] = useState<CatalogOption[]>([]);
  const [departamentOpts, setDepartamentOpts] = useState<CatalogOption[]>([]);
  const [carrecOpts, setCarrecOpts] = useState<CatalogOption[]>([]);
  const [tallaPantalonsOpts, setTallaPantalonsOpts] = useState<CatalogOption[]>([]);
  const [tallaCamisaOpts, setTallaCamisaOpts] = useState<CatalogOption[]>([]);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id) {
      loadEmployee(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const load = (path: string, setter: (opts: CatalogOption[]) => void) =>
      api.get<{ success: boolean; data: CatalogOption[] }>(path)
        .then((r) => setter((r.data ?? []).filter((o) => o.isActive)))
        .catch(() => setter([]));
    load('/catalog/relacio-laboral', setRelacioLaboralOpts);
    load('/catalog/tipus-contracte', setTipusContracteOpts);
    load('/catalog/departament', setDepartamentOpts);
    load('/catalog/carrec', setCarrecOpts);
    load('/catalog/talla-pantalons', setTallaPantalonsOpts);
    load('/catalog/talla-camisa', setTallaCamisaOpts);
  }, []);

  const loadEmployee = async (employeeId: string) => {
    try {
      setLoading(true);
      setError(null);

      const empRes = await api.get<{ success: boolean; data: Employee }>(`/employees/${employeeId}`);
      const emp = empRes.data;
      setEmployee(emp);
      setFormData({
        firstName: emp.firstName || '',
        lastName: emp.lastName || '',
        photoUrl: emp.photoUrl || null,
        dni: emp.dni || '',
        dniIssueDate: (emp as any).dniIssueDate ? (emp as any).dniIssueDate.split('T')[0] : '',
        dniExpiryDate: (emp as any).dniExpiryDate ? (emp as any).dniExpiryDate.split('T')[0] : '',
        socialSecurityNumber: emp.socialSecurityNumber || '',
        birthDate: emp.birthDate ? emp.birthDate.split('T')[0] : '',
        address: emp.address || '',
        city: emp.city || '',
        postalCode: emp.postalCode || '',
        phone: emp.phone || '',
        personalEmail: emp.personalEmail || '',
        workEmail: emp.workEmail || '',
        hireDate: emp.hireDate ? emp.hireDate.split('T')[0] : '',
        terminationDate: emp.terminationDate ? emp.terminationDate.split('T')[0] : '',
        contractType: (emp.contractType as EmployeeFormData['contractType']) || 'indefinite',
        department: emp.department || '',
        position: emp.position || '',
        category: emp.category || '',
        grossSalary: emp.grossSalary || 0,
        hourlyCost: emp.hourlyCost || 0,
        hourlyCostSaturday: (emp as any).hourlyCostSaturday || 0,
        hourlyCostHoliday: (emp as any).hourlyCostHoliday || 0,
        bonusOutsideBcn: (emp as any).bonusOutsideBcn || 0,
        correctionFactor: (emp as any).correctionFactor || 0,
        companySalaryCost: (emp as any).companySalaryCost || 0,
        grossHourlyCost: (emp as any).grossHourlyCost || 0,
        weeklyHours: emp.weeklyHours || 40,
        iban: emp.iban || '',
        drivingLicense: emp.drivingLicense || '',
        drivingLicenseExpiry: (emp as any).drivingLicenseExpiry ? (emp as any).drivingLicenseExpiry.split('T')[0] : '',
        emergencyContact: emp.emergencyContact || '',
        emergencyPhone: emp.emergencyPhone || '',
        notes: emp.notes || '',
        pantsSize: (emp as any).pantsSize || '',
        shirtSize: (emp as any).shirtSize || '',
        shoeSize: (emp as any).shoeSize || '',
        relacioLaboral: (emp as any).relacioLaboral || '',
        empresa: (emp as any).empresa || '',
        departamentEnum: (emp as any).departamentEnum || '',
        carrec: (emp as any).carrec || '',
        riskInfoDate: (emp as any).riskInfoDate ? (emp as any).riskInfoDate.split('T')[0] : '',
        machineAuthDate: (emp as any).machineAuthDate ? (emp as any).machineAuthDate.split('T')[0] : '',
        epiDeliveryDate: (emp as any).epiDeliveryDate ? (emp as any).epiDeliveryDate.split('T')[0] : '',
        medicalReviewDate: (emp as any).medicalReviewDate ? (emp as any).medicalReviewDate.split('T')[0] : '',
      });

      const [contractsRes, trainingsRes, docsRes] = await Promise.allSettled([
        api.get<{ success: boolean; data: Contract[] }>(`/employees/${employeeId}/contracts`),
        api.get<{ success: boolean; data: Training[] }>(`/trainings/employee/${employeeId}`),
        api.get<{ success: boolean; data: Document[] }>(`/employees/${employeeId}/documents`),
      ]);

      if (contractsRes.status === 'fulfilled') setContracts(contractsRes.value.data);
      if (trainingsRes.status === 'fulfilled') setTrainings(trainingsRes.value.data);
      if (docsRes.status === 'fulfilled') setDocuments(docsRes.value.data);
    } catch (err) {
      console.error('[v0] Error loading employee:', err);
      setError('Error en carregar les dades de l\'empleat');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof EmployeeFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (file: File) => {
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post<{ success: boolean; data: { photoUrl: string } }>(
        '/employees/upload-photo', fd, { headers: { 'Content-Type': undefined } }
      );
      handleChange('photoUrl', res.data.data.photoUrl);
    } catch (err: any) {
      alert('Error pujant la foto: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setPhotoDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handlePhotoUpload(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const cleanData: Record<string, any> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (value === '' || value === null || value === undefined) {
        continue;
      }
      if (['grossSalary', 'hourlyCost', 'hourlyCostSaturday', 'hourlyCostHoliday', 'bonusOutsideBcn', 'correctionFactor', 'weeklyHours', 'shoeSize'].includes(key)) {
        const num = parseFloat(value as string);
        if (!isNaN(num)) {
          cleanData[key] = num;
        }
      } else {
        cleanData[key] = value;
      }
    }
    // La neteja general salta terminationDate quan es buida (per tornar a Actiu cal poder
    // esborrar-la) — s'envia explicitament com a null perque el backend sap que, quan
    // aquest camp arriba, ha de reactivar l'empleat (vegeu employee.service.ts update()).
    if (!isNew) {
      cleanData.terminationDate = formData.terminationDate || null;
      // Mateix motiu que terminationDate: si s'ha eliminat la foto, cal enviar
      // explicitament null perque la neteja general no descarti el canvi.
      cleanData.photoUrl = formData.photoUrl || null;
    }
    if (cleanData.grossSalary) {
      cleanData.companySalaryCost = cleanData.grossSalary * (1 + (cleanData.correctionFactor || 0) / 100);
      const hoursPerYear = (cleanData.weeklyHours || 40) * 52;
      cleanData.grossHourlyCost = cleanData.companySalaryCost / hoursPerYear;
    }

    try {
      if (!isNew && id) {
        await api.put(`/employees/${id}`, cleanData);
      } else {
        await api.post<{ success: boolean; data: { id: string } }>('/employees', cleanData);
      }
      navigate('/rrhh');
    } catch (err: any) {
      console.error('[v0] Error saving employee:', err);
      setError(err.response?.data?.message || 'Error en guardar l\'empleat');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ca-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ca-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const isExpiringSoon = (dateStr?: string) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);
    return date <= thirtyDaysFromNow && date >= now;
  };

  const isExpired = (dateStr?: string) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error && !isNew && !employee) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] gap-4">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-destructive">{error}</p>
        <button
          onClick={() => navigate('/rrhh')}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
        >
          Tornar a RRHH
        </button>
      </div>
    );
  }

  const inputCls = 'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary';
  const labelCls = 'block text-sm font-medium mb-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Capçalera sticky amb botons d'acció sempre visibles — mateix patro que la resta de
          fitxes de l'ERP (Clients, Expedients Comercials, Pressupostos): una sola pantalla,
          sense mode "nomes lectura" separat. Fons blanc sòlid i sense icona de tornar, ja
          que "Cancel·lar" a la dreta fa la mateixa funció (estàndard 2026-11). */}
      <div className="sticky top-0 z-20 bg-white border-b flex items-center justify-between gap-4 py-3 -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-foreground truncate">
                {isNew ? 'Nou Empleat' : `${employee?.firstName} ${employee?.lastName}`}
              </h1>
              {!isNew && employee && (
                <Badge variant={statusConfig[employee.status]?.variant || 'default'}>
                  {statusConfig[employee.status]?.label || employee.status}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {isNew ? 'Afegeix un nou empleat al sistema' : `${employee?.employeeNumber} - ${employee?.dni}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate('/rrhh')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
          >
            Cancel·lar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Guardant...' : 'Guardar'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          {error}
        </div>
      )}

      {/* Resum */}
      {!isNew && employee && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Building2 className="h-8 w-8 text-primary opacity-80" />
                <div>
                  <p className="text-sm text-muted-foreground">Departament</p>
                  <p className="font-semibold">{employee.department || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Briefcase className="h-8 w-8 text-primary opacity-80" />
                <div>
                  <p className="text-sm text-muted-foreground">Carrec</p>
                  <p className="font-semibold">{employee.position || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Calendar className="h-8 w-8 text-primary opacity-80" />
                <div>
                  <p className="text-sm text-muted-foreground">Data Alta</p>
                  <p className="font-semibold">{formatDate(employee.hireDate)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Euro className="h-8 w-8 text-primary opacity-80" />
                <div>
                  <p className="text-sm text-muted-foreground">Cost/Hora</p>
                  <p className="font-semibold">{formatDecimal(Number(employee.hourlyCost || 0))} EUR</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs — nomes te sentit navegar-hi un cop l'empleat ja existeix */}
      {!isNew && (
        <div className="border-b">
          <nav className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {(isNew || activeTab === 'general') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Columna esquerra — dades principals, sempre visibles */}
          <div className="space-y-6">
            <Card className="rounded-xl">
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Dades Personals
                  </CardTitle>
                  {/* Foto de l'empleat — cantonada superior dreta de la targeta */}
                  <div className="w-20 shrink-0">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); e.target.value = ''; }}
                    />
                    {formData.photoUrl ? (
                      <div className="relative group w-20 h-20 rounded-lg border overflow-hidden bg-muted/50">
                        <img src={formData.photoUrl} alt="Foto empleat" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                          <button type="button" title="Substituir foto" onClick={() => photoInputRef.current?.click()}
                            className="bg-white/90 rounded-full p-1.5 hover:bg-white shadow">
                            <Upload className="h-3.5 w-3.5 text-slate-700" />
                          </button>
                          <button type="button" title="Eliminar foto" onClick={() => handleChange('photoUrl', null)}
                            className="bg-white/90 rounded-full p-1.5 hover:bg-white shadow">
                            <X className="h-3.5 w-3.5 text-red-600" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
                          photoDragOver ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                        onClick={() => photoInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setPhotoDragOver(true); }}
                        onDragLeave={() => setPhotoDragOver(false)}
                        onDrop={handlePhotoDrop}
                      >
                        {uploadingPhoto ? (
                          <span className="text-[9px] text-muted-foreground text-center px-1">Pujant...</span>
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-muted-foreground mb-0.5" />
                            <span className="text-[9px] text-muted-foreground text-center px-1">Foto</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Nom *</label>
                  <input type="text" required value={formData.firstName} onChange={(e) => handleChange('firstName', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Cognoms *</label>
                  <input type="text" required value={formData.lastName} onChange={(e) => handleChange('lastName', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>DNI/NIE *</label>
                  <input type="text" required value={formData.dni} onChange={(e) => handleChange('dni', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Numero SS</label>
                  <input type="text" value={formData.socialSecurityNumber} onChange={(e) => handleChange('socialSecurityNumber', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Data Naixement</label>
                  <input type="date" value={formData.birthDate} onChange={(e) => handleChange('birthDate', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Carnet Conduir</label>
                  <input type="text" placeholder="B, C, D..." value={formData.drivingLicense} onChange={(e) => handleChange('drivingLicense', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{"Data d'Emissio DNI"}</label>
                  <input type="date" value={formData.dniIssueDate} onChange={(e) => handleChange('dniIssueDate', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Data Caducitat DNI</label>
                  <input type="date" value={formData.dniExpiryDate} onChange={(e) => handleChange('dniExpiryDate', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Caducitat Carnet Conduir</label>
                  <input type="date" value={formData.drivingLicenseExpiry} onChange={(e) => handleChange('drivingLicenseExpiry', e.target.value)} className={inputCls} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Columna dreta — dades secundaries, colapsables (mateix component CollapsibleCard
              que ExpedientComercialForm.tsx i BudgetForm.tsx) */}
          <div className="space-y-6">
            <CollapsibleCard title="Contacte" icon={Phone} iconClassName="text-sky-600" defaultOpen>
              <div className="p-5 grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelCls}>Adreca</label>
                  <input type="text" value={formData.address} onChange={(e) => handleChange('address', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Codi Postal</label>
                  <input type="text" value={formData.postalCode} onChange={(e) => handleChange('postalCode', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Ciutat</label>
                  <input type="text" value={formData.city} onChange={(e) => handleChange('city', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Telefon</label>
                  <input type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Email Personal</label>
                  <input type="email" value={formData.personalEmail} onChange={(e) => handleChange('personalEmail', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Email Feina</label>
                  <input type="email" value={formData.workEmail} onChange={(e) => handleChange('workEmail', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Contacte Emergencia</label>
                  <input type="text" value={formData.emergencyContact} onChange={(e) => handleChange('emergencyContact', e.target.value)} className={inputCls} />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Telefon Emergencia</label>
                  <input type="tel" value={formData.emergencyPhone} onChange={(e) => handleChange('emergencyPhone', e.target.value)} className={inputCls} />
                </div>
              </div>
            </CollapsibleCard>

            <CollapsibleCard title="Dades Laborals" icon={Briefcase} iconClassName="text-blue-600">
              <div className="p-5 grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Relació Laboral</label>
                  <select value={formData.relacioLaboral} onChange={(e) => handleChange('relacioLaboral', e.target.value)} className={`${inputCls} bg-white`}>
                    <option value="">Selecciona...</option>
                    {relacioLaboralOpts.map((o) => <option key={o.id} value={o.code}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Empresa</label>
                  <input type="text" value={formData.empresa} onChange={(e) => handleChange('empresa', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Data Alta</label>
                  <input type="date" value={formData.hireDate} onChange={(e) => handleChange('hireDate', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Data Baixa</label>
                  <input type="date" value={formData.terminationDate} onChange={(e) => handleChange('terminationDate', e.target.value)} className={inputCls} />
                  <p className="text-xs text-muted-foreground mt-1">
                    Indicar una data dona de baixa l&apos;empleat. Esborrar-la el reactiva.
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Tipus Contracte</label>
                  <select value={formData.contractType} onChange={(e) => handleChange('contractType', e.target.value)} className={`${inputCls} bg-white`}>
                    {tipusContracteOpts.map((o) => <option key={o.id} value={o.code}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Hores Setmanals</label>
                  <input type="number" min="0" max="60" value={formData.weeklyHours} onChange={(e) => handleChange('weeklyHours', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Departament</label>
                  <select value={formData.departamentEnum} onChange={(e) => handleChange('departamentEnum', e.target.value)} className={`${inputCls} bg-white`}>
                    <option value="">Selecciona...</option>
                    {departamentOpts.map((o) => <option key={o.id} value={o.code}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Carrec</label>
                  <select value={formData.carrec} onChange={(e) => handleChange('carrec', e.target.value)} className={`${inputCls} bg-white`}>
                    <option value="">Selecciona...</option>
                    {carrecOpts.map((o) => <option key={o.id} value={o.code}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Categoria</label>
                  <input type="text" value={formData.category} onChange={(e) => handleChange('category', e.target.value)} className={inputCls} />
                </div>
              </div>
            </CollapsibleCard>

            <CollapsibleCard title="Dades Economiques" icon={Euro} iconClassName="text-emerald-600">
              <div className="p-5 grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Salari Brut Anual</label>
                  <input type="number" min="0" step="0.01" value={formData.grossSalary} onChange={(e) => handleChange('grossSalary', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Factor Correccio %</label>
                  <input type="number" min="0" max="100" step="0.01" placeholder="33.00" value={formData.correctionFactor} onChange={(e) => handleChange('correctionFactor', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Cost Salarial Empresa</label>
                  <input type="number" readOnly value={(formData.grossSalary * (1 + (formData.correctionFactor || 0) / 100)).toFixed(2)} className={`${inputCls} bg-muted/50 cursor-not-allowed`} />
                </div>
                <div>
                  <label className={labelCls}>Cost Hora Brut</label>
                  <input type="number" readOnly value={formData.weeklyHours > 0 ? ((formData.grossSalary * (1 + (formData.correctionFactor || 0) / 100)) / (formData.weeklyHours * 52)).toFixed(2) : '0.00'} className={`${inputCls} bg-muted/50 cursor-not-allowed`} />
                </div>
                <div>
                  <label className={labelCls}>Cost per Hora</label>
                  <input type="number" min="0" step="0.01" value={formData.hourlyCost} onChange={(e) => handleChange('hourlyCost', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Cost Hora Dissabte</label>
                  <input type="number" min="0" step="0.01" value={formData.hourlyCostSaturday} onChange={(e) => handleChange('hourlyCostSaturday', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Cost Hora Festiu</label>
                  <input type="number" min="0" step="0.01" value={formData.hourlyCostHoliday} onChange={(e) => handleChange('hourlyCostHoliday', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Prima Fora BCN</label>
                  <input type="number" min="0" step="0.01" value={formData.bonusOutsideBcn} onChange={(e) => handleChange('bonusOutsideBcn', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>IBAN</label>
                  <input type="text" placeholder="ES00 0000 0000 0000 0000 0000" value={formData.iban} onChange={(e) => handleChange('iban', e.target.value)} className={inputCls} />
                </div>
              </div>
            </CollapsibleCard>

            <CollapsibleCard title="Talles Roba i Peu" icon={Shirt} iconClassName="text-purple-600">
              <div className="p-5 grid grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Pantalons</label>
                  <select value={formData.pantsSize} onChange={(e) => handleChange('pantsSize', e.target.value)} className={`${inputCls} bg-white`}>
                    <option value="">Selecciona...</option>
                    {tallaPantalonsOpts.map((o) => <option key={o.id} value={o.code}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Camisa/Dessuadora</label>
                  <select value={formData.shirtSize} onChange={(e) => handleChange('shirtSize', e.target.value)} className={`${inputCls} bg-white`}>
                    <option value="">Selecciona...</option>
                    {tallaCamisaOpts.map((o) => <option key={o.id} value={o.code}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Numero Peu</label>
                  <select value={String(formData.shoeSize)} onChange={(e) => handleChange('shoeSize', e.target.value ? parseInt(e.target.value) : '')} className={`${inputCls} bg-white`}>
                    <option value="">Selecciona...</option>
                    {Array.from({ length: 19 }, (_, i) => (
                      <option key={36 + i} value={String(36 + i)}>{36 + i}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CollapsibleCard>

            <CollapsibleCard title="Seguretat i Salut" icon={HardHat} iconClassName="text-amber-600">
              <div className="p-5 grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Informació de Riscos</label>
                  <input type="date" value={formData.riskInfoDate} onChange={(e) => handleChange('riskInfoDate', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Autorització Maquinària</label>
                  <input type="date" value={formData.machineAuthDate} onChange={(e) => handleChange('machineAuthDate', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Lliurament EPI</label>
                  <input type="date" value={formData.epiDeliveryDate} onChange={(e) => handleChange('epiDeliveryDate', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Revisio Medica</label>
                  <input type="date" value={formData.medicalReviewDate} onChange={(e) => handleChange('medicalReviewDate', e.target.value)} className={inputCls} />
                </div>
              </div>
            </CollapsibleCard>

            <CollapsibleCard title="Observacions" icon={MessageSquare} iconClassName="text-teal-600">
              <div className="p-5">
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                  placeholder="Notes addicionals sobre l'empleat..."
                  className={`${inputCls} resize-none`}
                />
              </div>
            </CollapsibleCard>
          </div>
        </div>
      )}

      {!isNew && employee && activeTab === 'calendar' && (
        <Card className="rounded-xl">
          <CardContent className="pt-6">
            <EmployeeCalendar
              employeeId={id!}
              employeeName={`${employee.firstName} ${employee.lastName}`}
            />
          </CardContent>
        </Card>
      )}

      {!isNew && activeTab === 'contracts' && (
        <Card className="rounded-xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Historial de Contractes</CardTitle>
            <button
              type="button"
              onClick={() => {/* TODO: Modal nou contracte */}}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg"
            >
              <Plus className="h-4 w-4" />
              Nou Contracte
            </button>
          </CardHeader>
          <CardContent>
            {contracts.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No hi ha contractes registrats</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipus</TableHead>
                    <TableHead>Data Inici</TableHead>
                    <TableHead>Data Fi</TableHead>
                    <TableHead>Salari Brut</TableHead>
                    <TableHead>Cost/Hora</TableHead>
                    <TableHead>Estat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contracts.map((contract) => (
                    <TableRow key={contract.id}>
                      <TableCell>{contractTypeLabels[contract.type] || contract.type}</TableCell>
                      <TableCell>{formatDate(contract.startDate)}</TableCell>
                      <TableCell>{formatDate(contract.endDate)}</TableCell>
                      <TableCell>{formatCurrency(contract.grossSalary)}</TableCell>
                      <TableCell>{formatDecimal(Number(contract.hourlyCost || 0))} EUR</TableCell>
                      <TableCell>
                        <Badge variant={contract.isCurrent ? 'success' : 'outline'}>
                          {contract.isCurrent ? 'Actual' : 'Anterior'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {!isNew && activeTab === 'training' && (
        <Card className="rounded-xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Formacions</CardTitle>
            <button
              type="button"
              onClick={() => navigate(`/rrhh/training/assign?employeeId=${id}`)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg"
            >
              <Plus className="h-4 w-4" />
              Assignar Formació
            </button>
          </CardHeader>
          <CardContent>
            {trainings.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No hi ha formacions registrades</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Formació</TableHead>
                    <TableHead>Tipus</TableHead>
                    <TableHead>Data Inici</TableHead>
                    <TableHead>Data Fi</TableHead>
                    <TableHead>Caducitat</TableHead>
                    <TableHead>Estat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trainings.map((training) => {
                    const statusInfo = trainingStatusConfig[training.status] || trainingStatusConfig.scheduled;
                    const StatusIcon = statusInfo.icon;
                    const expiring = isExpiringSoon(training.expiryDate);
                    const expired = isExpired(training.expiryDate);

                    return (
                      <TableRow key={training.id}>
                        <TableCell className="font-medium">{training.training.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{training.training.type}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(training.startDate)}</TableCell>
                        <TableCell>{formatDate(training.endDate)}</TableCell>
                        <TableCell>
                          <span className={expired ? 'text-red-500' : expiring ? 'text-amber-500' : ''}>
                            {formatDate(training.expiryDate)}
                            {expired && ' (Caducada)'}
                            {expiring && !expired && ' (A punt de caducar)'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className={`flex items-center gap-1 ${statusInfo.color}`}>
                            <StatusIcon className="h-4 w-4" />
                            {statusInfo.label}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {!isNew && activeTab === 'documents' && (
        <Card className="rounded-xl">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Documents</CardTitle>
            <button
              type="button"
              onClick={() => {/* TODO: Modal pujar document */}}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg"
            >
              <Plus className="h-4 w-4" />
              Pujar Document
            </button>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No hi ha documents registrats</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Tipus</TableHead>
                    <TableHead>Data Document</TableHead>
                    <TableHead>Caducitat</TableHead>
                    <TableHead className="text-right">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell className="font-medium">{doc.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{doc.type}</Badge>
                      </TableCell>
                      <TableCell>{formatDate(doc.documentDate)}</TableCell>
                      <TableCell>
                        <span className={isExpired(doc.expiryDate) ? 'text-red-500' : isExpiringSoon(doc.expiryDate) ? 'text-amber-500' : ''}>
                          {formatDate(doc.expiryDate)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <button type="button" className="p-2 hover:bg-accent rounded-lg">
                          <Download className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {!isNew && activeTab === 'time' && (
        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle>Imputacions de Temps</CardTitle>
            <CardDescription>Hores imputades a projectes i ordres de treball</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground py-8">
              Les imputacions de temps es mostren a la secció &quot;Imputació d&apos;Hores&quot;
            </p>
          </CardContent>
        </Card>
      )}
    </form>
  );
}
